import React, { useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { downloadReport } from "@/lib/pdf";

const COLORS = ["#D4AF37", "#E5C86B", "#F59E0B", "#10B981", "#3B82F6", "#A78BFA", "#A1A1AA"];

export default function ReportsPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/reports/sales").then((r) => setStats(r.data)); }, []);

  if (!stats) return <div className="p-10 text-zinc-500">Loading…</div>;

  const monthData = stats.by_month || [];
  const statusData = Object.entries(stats.by_status || {}).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));

  const exportPDF = () => {
    downloadReport({
      title: "Sales Report",
      filename: `Sales-Report-${new Date().toISOString().slice(0, 10)}.pdf`,
      subtitle: `Generated on ${new Date().toLocaleDateString("en-MY", { dateStyle: "long" })}`,
      kpis: [
        { label: "Total Orders", value: stats.total_orders },
        { label: "Total Revenue", value: fmtCurrency(stats.total_revenue) },
        { label: "Collected", value: fmtCurrency(stats.total_collected) },
        { label: "Outstanding", value: fmtCurrency(stats.outstanding) },
      ],
      sections: [
        {
          title: "Revenue by Month",
          columns: ["Month", "Revenue"],
          rows: monthData.map((m) => [m.month, fmtCurrency(m.amount)]),
          columnStyles: { 1: { halign: "right" } },
        },
        {
          title: "Pipeline by Status",
          columns: ["Status", "Count"],
          rows: statusData.map((s) => [s.name, String(s.value)]),
          columnStyles: { 1: { halign: "right" } },
        },
      ],
    });
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="reports-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Business</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Sales Reports</h1>
        </div>
        <Button onClick={exportPDF} data-testid="report-pdf" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
          <Download className="w-4 h-4 mr-1.5" /> Download PDF
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Tile label="Total Orders" value={stats.total_orders} />
        <Tile label="Total Revenue" value={fmtCurrency(stats.total_revenue)} />
        <Tile label="Collected" value={fmtCurrency(stats.total_collected)} tint="#10B981" />
        <Tile label="Outstanding" value={fmtCurrency(stats.outstanding)} tint="#F59E0B" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-lux p-6 lg:col-span-2">
          <div className="label-eyebrow">Revenue · Monthly</div>
          <h3 className="font-display text-xl mt-1 mb-4">Trend</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthData}>
                <CartesianGrid strokeDasharray="2 4" stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="month" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 4, fontSize: 12 }} />
                <Line type="monotone" dataKey="amount" stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-lux p-6">
          <div className="label-eyebrow">Pipeline</div>
          <h3 className="font-display text-xl mt-1 mb-4">Distribution</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={70} innerRadius={40} paddingAngle={2}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 4, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, tint }) {
  return (
    <div className="card-lux p-5">
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-3xl mt-2" style={{ color: tint || "#FAFAFA" }}>{value}</div>
    </div>
  );
}
